/*
  Copyright (c) 2026 Fluxonix Inc. All rights reserved.
*/

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";

const ConfirmAction = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [task, setTask] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("medium");

  useEffect(() => {
    let isMounted = true;

    const fetchSubmission = async () => {
      try {
        const res = await api.get(`/secure/submissions/${submissionId}`);
        const data = res.data;

        const action = data?.suggested_action || {};

        if (isMounted) {
          setTask(action.task || data?.translated_transcript || "");
          setAssignee(action.assignee || "");
          setPriority(action.priority || "medium");
        }
      } catch (e) {
        console.error("Failed to fetch submission", e);
        if (isMounted) {
          setError("Failed to load submission");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSubmission();

    return () => {
      isMounted = false;
    };
  }, [submissionId]);

  const handleConfirm = async () => {
    try {
      await api.post("/secure/actions/confirm", {
        submission_id: submissionId,
        task,
        assignee,
        priority,
      });

      sessionStorage.setItem("lastHandledAction", submissionId);
      navigate("/lpo-entry-mode");
    } catch (e) {
      console.error("Failed to confirm action", e);
      setError("Failed to send action");
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          <span className="text-sm font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-4 p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3 w-full max-w-lg">
          <span className="material-symbols-outlined text-red-500">error</span>
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <span className="material-symbols-outlined">task_alt</span>
          </div>
          <h3 className="font-bold text-slate-900 dark:text-white text-lg">Confirm Action</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          We detected an action from your submission. Please confirm before sending.
        </p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Task</label>
            <textarea
              value={task}
              onChange={(e) => setTask(e.target.value)}
              rows={4}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all p-2.5"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Assignee</label>
            <input
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all p-2.5"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all p-2.5"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-red-500">error</span>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <span className="material-symbols-outlined text-red-400 hover:text-red-600">close</span>
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleConfirm}
            className="flex-1 py-3 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
          >
            Confirm & Send
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAction;